import { z } from "zod";

/* ── Rich-text formatting (bold/italic/underline/…) for «@bing» broadcasts ──────
 *
 * The admin composes a broadcast IN TELEGRAM with native formatting; the bot receives it as
 * `message.entities` (a `MessageEntity[]` describing style RANGES over the text, offsets in
 * UTF-16 code units). We store a SANITIZED, BOUNDED subset of those ranges and the mini app
 * renders them by building React nodes from the structured data — never HTML, so there is NO
 * injection surface even though the content is admin-authored. */

/** The formatting range types we honor (a safe subset of Telegram's MessageEntity types). */
export const INBOX_ENTITY_TYPES = [
  "bold",
  "italic",
  "underline",
  "strikethrough",
  "code",
  "text_link",
] as const;
export type InboxEntityType = (typeof INBOX_ENTITY_TYPES)[number];

/** Hard cap on formatting ranges per message — a bound so one broadcast can't write a huge
 *  blob onto every recipient row (the fan-out denormalizes this, like the text). */
export const MAX_INBOX_ENTITIES = 200;

/** One sanitized formatting range: a type over `[offset, offset+length)` (UTF-16 units into
 *  the message text), plus a validated http(s) `url` for `text_link`. */
export const inboxTextEntitySchema = z.object({
  type: z.enum(INBOX_ENTITY_TYPES),
  offset: z.number().int().nonnegative(),
  length: z.number().int().positive(),
  /** Present ONLY for `text_link` — an http(s) URL (validated by the sanitizer). */
  url: z.string().url().optional(),
});
export type InboxTextEntity = z.infer<typeof inboxTextEntitySchema>;

/** True for an absolute http(s) URL — the only thing a broadcast `text_link` may point at
 *  (mirrors the monetization `safeUrlRef` guard: no `javascript:`/`data:`/`vbscript:`). */
export function isSafeBroadcastUrl(value: unknown): value is string {
  return typeof value === "string" && value.length <= 2048 && /^https?:\/\//i.test(value.trim());
}

/**
 * Turn RAW Telegram message entities into the sanitized, bounded {@link InboxTextEntity} list we
 * persist + render. Drops anything not in {@link INBOX_ENTITY_TYPES}; clamps every range to the
 * text bounds; requires a valid http(s) `url` for `text_link`; caps the count at
 * {@link MAX_INBOX_ENTITIES}. `text.length` is measured in UTF-16 code units, matching Telegram's
 * offset space exactly, so a JS `String.slice(offset, offset+length)` reproduces the range.
 */
export function sanitizeInboxEntities(text: string, raw: unknown): InboxTextEntity[] {
  if (!Array.isArray(raw)) return [];
  const textLen = text.length; // UTF-16 units — Telegram's offset space
  const allowed = new Set<string>(INBOX_ENTITY_TYPES);
  const out: InboxTextEntity[] = [];
  for (const e of raw) {
    if (out.length >= MAX_INBOX_ENTITIES) break;
    if (!e || typeof e !== "object") continue;
    const type = (e as { type?: unknown }).type;
    const offset = (e as { offset?: unknown }).offset;
    const length = (e as { length?: unknown }).length;
    if (typeof type !== "string" || !allowed.has(type)) continue;
    if (!Number.isInteger(offset) || !Number.isInteger(length)) continue;
    const off = offset as number;
    const len = length as number;
    if (off < 0 || len <= 0 || off >= textLen) continue;
    const clampedLen = Math.min(len, textLen - off);
    if (clampedLen <= 0) continue;
    if (type === "text_link") {
      const url = (e as { url?: unknown }).url;
      if (!isSafeBroadcastUrl(url)) continue; // an unlinkable/unsafe link → drop the range
      out.push({ type, offset: off, length: clampedLen, url: (url as string).trim() });
    } else {
      out.push({ type: type as InboxEntityType, offset: off, length: clampedLen });
    }
  }
  return out;
}

/**
 * A user's in-app inbox message (as shown in the Messages screen). The SENDER is always
 * the «@bing» system identity (see {@link BING_SYSTEM_IDENTITY}) — inbox messages are
 * system/broadcast notices, not peer DMs — so it isn't carried per-row.
 */
export const inboxMessageViewSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  text: z.string(),
  /** Formatting ranges over `text` (bold/italic/…); empty when the message is plain. */
  entities: z.array(inboxTextEntitySchema).default([]),
  /** Capability id of an attached broadcast picture (`GET /inbox-image/:id`), or null. */
  imageId: z.string().nullable(),
  read: z.boolean(),
  /** ISO timestamp. */
  createdAt: z.string(),
});
export type InboxMessageView = z.infer<typeof inboxMessageViewSchema>;

/** The inbox listing + the caller's unread count (for the nav badge). */
export const inboxListSchema = z.object({
  messages: z.array(inboxMessageViewSchema),
  unread: z.number().int().nonnegative(),
});
export type InboxList = z.infer<typeof inboxListSchema>;

/** Admin: compose an in-app broadcast (fanned out to every user's inbox as «@bing»). */
export const inboxBroadcastInputSchema = z.object({
  title: z.string().trim().max(120).optional(),
  text: z.string().trim().min(1).max(4000),
});
export type InboxBroadcastInput = z.infer<typeof inboxBroadcastInputSchema>;

/** Admin: the result of an in-app broadcast (how many inboxes it landed in). */
export const inboxBroadcastResultSchema = z.object({
  delivered: z.number().int().nonnegative(),
});
export type InboxBroadcastResult = z.infer<typeof inboxBroadcastResultSchema>;
